(ns pfo.chat.component
  (:require [reagent.core :as r :refer [atom track!] :refer-macros [with-let]]
            [dommy.core :as dommy :refer-macros [sel]]
            [promesa.core :as p]
            [pfo.util :as util]
            ["react" :as react]
            ["react-markdown" :default ReactMarkdown]
            ["remark-gfm" :default remarkGfm]
            ["rehype-highlight" :default rehypeHighlight]
            ["@chatscope/chat-ui-kit-react" :refer [MainContainer ChatContainer MessageList Message MessageInput TypingIndicator InputToolbox Sidebar ExpansionPanel]]
            ["react-dropzone" :refer [useDropzone]]
            #_:clj-kondo/ignore [taoensso.timbre :as timbre
                                 :refer [log trace debug info warn error fatal report logf
                                         tracef debugf infof warnf errorf fatalf reportf spy]]
            [clojure.string :as str]
            [lentes.core :as l]
            [pfo.api :as api]
            [pfo.controller :as controller :refer [subscribe]]
            [pfo.flash :refer [set-flash]]
            [pfo.common.component :as common]
            [pfo.chat.model :as model]
            [pfo.chat.tool-component :as tool]
            [pfo.chat.common-component :refer [markdown-message]]
            [pfo.chat.feedback-component :as feedback]
            [pfo.flag :refer [enabled]]
            [pfo.collection.model :as collection-model]
            [pfo.state :as state]
            [pfo.form :refer [option-value set-selected]]
            [pfo.assignment.component :refer [assignments-edit-modal assignments-to-id-set get-initial-ui-assignments]]
            [ajax.core]
            [ajax.formats :refer [text-response-format]]
            [pfo.assignment.component :as assignment]
            [pfo.notify :as notify]))

(defonce ^:private aark-status (atom nil))
(defonce ^:private resolved-timeout (atom nil))
(def ^:private resolved-banner-dwell-ms 30000)

(notify/register-handler "ai_status"
  (fn [payload]
    (if (= (:status payload) "ok")
      ;; Guard: only enter "resolved" when coming from an active outage/partial.
      ;; A redundant "ok" during the 30s dwell (e.g. SSE reconnect) must not
      ;; restart the timer and extend the display time.
      ;; "network" status clears immediately — it is a local connectivity issue,
      ;; not a service outage, so no "Service Outage Fixed" dwell is shown.
      (let [prev-status (:status @aark-status)]
        (when (and prev-status (not= prev-status "resolved"))
          (when @resolved-timeout
            (js/clearTimeout @resolved-timeout)
            (reset! resolved-timeout nil))
          (if (= prev-status "network")
            (reset! aark-status nil)
            (do
              (reset! aark-status {:status "resolved"})
              (reset! resolved-timeout
                      (js/setTimeout #(reset! aark-status nil) resolved-banner-dwell-ms))))))
      (do
        (when @resolved-timeout
          (js/clearTimeout @resolved-timeout)
          (reset! resolved-timeout nil))
        (reset! aark-status payload)))))

(declare new-chat)

(defn upload-file [chat-state file]
  (let [file-data {:name (.-name file)
                   :size (.-size file)
                   :type (.-type file)}
        form-data (js/FormData.)]
    (.append form-data "file" file (.-name file))
    (swap! chat-state assoc :uploading? true)
    (controller/dispatch :upload-file
                         :load {:form-data form-data
                                :on-success (fn [response]
                                              (swap! chat-state
                                                     (fn [state]
                                                       (-> state
                                                           (assoc :uploading? false)
                                                           (update :attached-files conj (assoc file-data :upload-id (:id response)))))))
                                :on-error (fn [error]
                                            (swap! chat-state
                                                   (fn [state]
                                                     (-> state
                                                         (assoc :uploading? false))))
                                            (throw error))})))

(defn file-item [options file]
  [:div.file-item
   [:span.file-icon
    (cond
      (.startsWith (:type file) "image/") "🖼️"
      (.includes (:type file) "pdf") "📄"
      (.includes (:type file) "word") "📝"
      (.includes (:type file) "excel") "📊"
      :else "📎")]
   [:div.file-info
    [:div.file-name (:name file)]
    [:div.file-size (str (.toFixed (/ (:size file) 1024) 1) " KB")]]
   (when (:remove-file-fn options)
     [:button.file-remove-btn
      {:on-click #((:remove-file-fn options) (:upload-id file))} "×"])])

(defn attached-files-preview [chat-state]
  (let [remove-file-fn (fn [upload-id]
                         (swap! chat-state update :attached-files
                                (fn [files]
                                  (remove #(= (:upload-id %) upload-id) files))))]
    (when (seq (:attached-files @chat-state))
      [:div.attached-files-compact
       [:div.files-list
        (for [file (:attached-files @chat-state)]
          ^{:key (:id file)}
          [file-item {:remove-file-fn remove-file-fn} file])]])))

(defn message-files [options files]
  [:div.attached-files-compact
   [:div.files-list
    (for [file files]
      ^{:key (:id file)}
      [file-item options file])]])

(defn dropzone-overlay [chat-state]
  (fn []
    (let [[dragging? set-dragging!] (react/useState false)
          dz (useDropzone
              #js {:onDrop (fn [accepted-files]
                             (doseq [f accepted-files]
                               (upload-file chat-state f))
                             (set-dragging! false))
                   :noClick true
                   :noKeyboard true
                   :multiple true
                   :maxSize (* 10 1024 1024)})
          getRootProps (.-getRootProps dz)
          getInputProps (.-getInputProps dz)
          isDragActive (.-isDragActive dz)]

      (react/useEffect
       (fn []
         (let [drag-out-timer (atom nil)
               clear-drag (fn []
                            (when @drag-out-timer
                              (js/clearTimeout @drag-out-timer)
                              (reset! drag-out-timer nil))
                            (set-dragging! false))

               on-window-dragenter (fn [e]
                                     (when (some? (.. e -dataTransfer -types))
                                       (set-dragging! true)))

               on-window-dragover (fn [e]
                                    (.preventDefault e)
                                    (when @drag-out-timer
                                      (js/clearTimeout @drag-out-timer))
                                    (reset! drag-out-timer (js/setTimeout clear-drag 150)))

               on-window-drop (fn [e]
                                (.preventDefault e)
                                (clear-drag))

               on-window-dragleave (fn [_]
                                     (when @drag-out-timer
                                       (js/clearTimeout @drag-out-timer))
                                     (reset! drag-out-timer (js/setTimeout clear-drag 150)))]

           (.addEventListener js/window "dragenter" on-window-dragenter)
           (.addEventListener js/window "dragover" on-window-dragover)
           (.addEventListener js/window "drop" on-window-drop)
           (.addEventListener js/window "dragleave" on-window-dragleave)

           (fn []
             (.removeEventListener js/window "dragenter" on-window-dragenter)
             (.removeEventListener js/window "dragover" on-window-dragover)
             (.removeEventListener js/window "drop" on-window-drop)
             (.removeEventListener js/window "dragleave" on-window-dragleave)))))

      (r/as-element
       (let [active (or dragging? isDragActive)]
         [:div.fullscreen-dropzone
          (merge (js->clj (getRootProps) :keywordize-keys true)
                 {:class (when active "active")})
          [:input (js->clj (getInputProps) :keywordize-keys true)]
          (when active
            [:div.dropzone-message
             [:div.icon "📁"]
             [:h2 "Drag files here to upload"]
             [:p "PDF, images, Word, Excel (max 10MB)"]])])))))

(defn message-send [options chat-state message-atom]
  (when (or (not (str/blank? @message-atom))
            (seq (:attached-files @chat-state)))
    (let [current-conversation-id (:current-conversation-id @chat-state)
          is-first-conversation? (empty? (:messages @chat-state))
          is-already-on-top? (or (nil? current-conversation-id) (= current-conversation-id (:id (first (:conversations options)))))
          send (fn [message-atom]
                 (let [msg {:direction "outgoing"
                            :sender model/user
                            :message @message-atom
                            :position "single"
                            :sentTime (js/Date.)
                            :files (:attached-files @chat-state)}
                       pending-text @message-atom
                       pending-files (:files msg)]
                   (swap! chat-state
                          (fn [state]
                            (-> state
                                (assoc :loading? true)
                                (assoc :turn-state nil :turn-step 0 :turn-step-base 0 :turn-failed? false :turn-elapsed 0)
                                (assoc :pending-message pending-text)
                                (assoc :pending-files pending-files)
                                (assoc :input-error nil))))
                   (controller/dispatch :message-send :load {:token (:token @chat-state)
                                                             :message @message-atom
                                                             :upload-ids (vec (map :upload-id (:files msg)))
                                                             :on-success (fn [message-obj]
                                                                           (swap! chat-state
                                                                                  (fn [state]
                                                                                    (-> state
                                                                                        (update :messages conj (merge {:message-obj message-obj
                                                                                                                       :id (:id message-obj)} msg))
                                                                                        (update :attached-files empty)
                                                                                        (update :input-content dissoc current-conversation-id))))
                                                                           (reset! message-atom nil)
                                                                           (when (or is-first-conversation? (not is-already-on-top?))
                                                                             (controller/dispatch :conversations :load
                                                                                                  {:on-success (fn [resp]
                                                                                                                 (swap! chat-state
                                                                                                                        assoc
                                                                                                                        :current-conversation-id
                                                                                                                        (:id (first resp))))})))
                                                             :on-error (fn [error]
                                                                         (let [pending       (:pending-message @chat-state)
                                                                               pending-files (:pending-files @chat-state)
                                                                               ;; Only the locked-conversation refusal gets its reason
                                                                               ;; relayed (#3106); any other 400 keeps the generic retry
                                                                               ;; advice — a future validation error must not offer a
                                                                               ;; new chat instead of a retry.
                                                                               rejection     (when (and (= 400 (:status error))
                                                                                                        (= "ConversationLocked" (get-in error [:response :body :type])))
                                                                                               (get-in error [:response :body :message]))]
                                                                           (swap! chat-state
                                                                                  (fn [state]
                                                                                    (-> state
                                                                                        (assoc :loading? false)
                                                                                        (assoc :pending-message nil)
                                                                                        (assoc :pending-files nil)
                                                                                        (assoc :attached-files pending-files)
                                                                                        (assoc :input-error (if rejection
                                                                                                              {:type :rejected :message rejection}
                                                                                                              {:type :transient})))))
                                                                           (reset! message-atom pending)))})))]
      (send message-atom))))

(defn chat-input-banner [chat-state]
  (let [state @chat-state]
    (if (and (:loading? state) (model/slow-turn? state))
      [:div.cs-input-banner-wrapper
       [common/notice-banner {:theme       :information
                              :description "This step is taking longer than usual. AARK™ is still working on your request, please hold on."}]]
      (when-let [{:keys [type message]} (:input-error state)]
        [:div.cs-input-banner-wrapper
         (case type
         :transient
         [common/notice-banner {:theme       :warning
                                :title       "Failed to send message"
                                :description "Something went wrong sending your message, please try again."}]
         ;; The server refused the message as conversation-locked and said why
         ;; (#3106) — relay its reason; a retry here meets the same refusal, so
         ;; offer a fresh chat instead.
         :rejected
         [common/notice-banner {:theme       :warning
                                :title       "Message not sent"
                                :description message
                                :action-text "Start new chat"
                                :on-action   (fn []
                                               (reset! chat-state new-chat)
                                               (util/set-location "#/chat"))}]
         ;; The queue worker refused a message that was queued before the
         ;; conversation was locked read-only (#3106). A retry meets the same
         ;; refusal, so the banner offers a fresh chat instead.
         :novice-locked
         [common/notice-banner {:theme       :warning
                                :title       "Message not sent"
                                :description model/novice-locked-message
                                :action-text "Start new chat"
                                :on-action   (fn []
                                               (reset! chat-state new-chat)
                                               (util/set-location "#/chat"))}]
         ;; Only for an error loaded from the database. It can be older than the server
         ;; change that trims an oversized tool result, so a retry can now work. It can
         ;; also still fail, so keep the "Start new chat" button here too (#3079).
         :context-limit
         [common/notice-banner {:theme       :warning
                                :title       "Context limit reached"
                                :description model/context-limit-message
                                :action-text "Start new chat"
                                :on-action   (fn []
                                               (reset! chat-state new-chat)
                                               (util/set-location "#/chat"))}]
         ;; Old rows only: the server stored this text before it learned to
         ;; repair the history instead of refusing it, and 0298 repaired them.
         :locked-earlier
         [common/notice-banner {:theme       :warning
                                :title       "Chat could not continue"
                                :description model/locked-earlier-message
                                :action-text "Start new chat"
                                :on-action   (fn []
                                               (reset! chat-state new-chat)
                                               (util/set-location "#/chat"))}]
         ;; The message was sent and the turn ran — it only failed to finish in
         ;; time. Deliberately not "try again": an identical over-budget request
         ;; meets the same deadline, so that advice sends the user into a loop.
         :timeout
         [common/notice-banner {:theme       :warning
                                :title       "Response timed out"
                                :description "AARK™ ran out of time on this request. Ask for a smaller part of it — fewer documents, or a shorter report — and it can carry on from what it already gathered."}]
         :service-interruption
         [common/notice-banner {:theme       :error
                                :title       "Service interruption"
                                :description "AARK™ is currently unavailable due to an issue on our end. Our team is investigating and working to restore service."}]
         :degraded
         [common/notice-banner {:theme       :warning
                                :title       "Incomplete response"
                                :description "AARK™'s last response may be incomplete. You can ask it to continue, or try regenerating the response."}]
         :unrecoverable
         [common/notice-banner {:theme       :error
                                :title       "Chat cannot continue"
                                :description "This conversation cannot be continued. Start a new chat to proceed."
                                :action-text "Start new chat"
                                :on-action   (fn []
                                               (reset! chat-state new-chat)
                                               (util/set-location "#/chat"))}]
         (when-let [copy (model/banner-copy type)]
           [common/notice-banner copy]))]))))

(defn aark-outage-banner []
  (when-let [{:keys [status source retry-count]} @aark-status]
    (let [partial?       (= status "partial")
          resolved?      (= status "resolved")
          network?       (= status "network")
          network-stuck? (and network? (>= (or retry-count 0) 5))]
      [common/notice-banner
       (cond-> {:theme       (cond resolved? :success partial? :warning :else :error)
                :title       (cond resolved? "Service Outage Fixed" partial? "AARK™ partially restored" :else "AARK™ service interruption")
                :description (cond
                               resolved?              "AARK™ is back. Normal service has resumed. You can continue your conversation in this chat or start a new one."
                               partial?               "AARK™ service is being restored. You may experience intermittent issues — please try again."
                               network-stuck?         "Connection still interrupted. If the problem persists, try refreshing the page."
                               network?               "Connection interrupted. Reconnecting..."
                               (= source "anthropic") "AARK™ is currently unavailable due to a disruption with Anthropic's AI service. We're monitoring the situation and will update this banner as status changes."
                               :else                  "AARK™ is currently unavailable due to an issue on our end. Our team is investigating and working to restore service.")}
         resolved? (assoc :link-text "Start new chat" :link-href "#/chat"))])))

(defn error-message [chat-state]
  (when-let [error (:error @chat-state)]
    [:div.cs-error-banner
     [:span error]
     [:button.cs-close-error {:on-click #(swap! chat-state assoc :error nil)} "×"]]))

(defn- report-chat-error [context response]
  ;; An exception thrown in on-success arrives here too, and it has no status.
  (api/report-error :async nil (str context " " (if (:status response)
                                                  (pr-str (select-keys response [:status :failure :status-text]))
                                                  (ex-message response)))
                    nil))

(defn- show-chat-error! [chat-state context response url-conversation?]
  (when-let [{:keys [message redirect?]} (model/request-error-action (:status response) url-conversation?)]
    (if redirect?
      (do (set-flash :error message)
          ;; Back must not land on the bad URL again.
          (.replace js/location "#/chat"))
      (do (report-chat-error context response)
          (swap! chat-state assoc :error message)))))

(defn parse-suggested-responses
  "Returns the suggested-response strings from a suggested_responses message.
  The LLM sometimes emits `responses` as a JSON-encoded string instead of an
  array (#2966); left unguarded the caller iterates that string per-character
  and renders one button per letter. A stringified array is re-parsed when it
  is valid JSON; anything still unrecoverable yields no buttons rather than
  garbage."
  [content]
  (let [parsed (try (util/to-clj (js/JSON.parse content)) (catch :default _ nil))
        raw    (:responses parsed)
        coll   (cond
                 (sequential? raw) raw
                 (string? raw)     (try
                                     (let [inner (util/to-clj (js/JSON.parse raw))]
                                       (when (sequential? inner) inner))
                                     (catch :default _ nil))
                 :else             nil)]
    (filterv string? coll)))

(defn suggested-responses-message [{:keys [is-last
                                           send-message-fn]}
                                   message-obj]
  [:div.c-suggested-responses
   (doall
     (for [response (parse-suggested-responses (:content message-obj))]
       [:button.c-suggested-responses__option
        (merge
          {:key response
           :on-click (util/handler #(send-message-fn response))}
          (when-not is-last {:disabled :disabled}))
        response]))])

(defn tool-result [{:keys [type title details]}]
  (let [expanded? (atom false)
        has-details? (some? details)
        confirm? (= type :confirm)
        action-class (if confirm?
                       "cs-action-confirm"
                       "cs-action-deny")
        icon-src (if confirm?
                   "/static/img/icons/aark-confirm.svg"
                   "/static/img/icons/aark-deny.svg")]
    (fn []
      [:div.cs-action-wrapper
       [:div {:class action-class}
        [:div.cs-action-content
         [:img.svg-icon--md {:src icon-src}]
         [:span title]]
        (when has-details?
          [:img.cs-action-arrow
           {:src (if @expanded?
                   "/static/img/icons/aark-collapse.svg"
                   "/static/img/icons/aark-expand.svg")
            :on-click #(swap! expanded? not)}])]
       (when (and has-details? @expanded?)
         [:div.cs-action-details
          details])])))

; Returns the confirm button label for a given set of tool calls.
; To customise the label for a new tool, add a branch before the :else clause.
(defn- confirm-button-label [tool-calls multiple]
  (cond
    multiple "Confirm All"
    (= model/tool-generate-document-summary (:tool_name (first tool-calls))) "Generate summary"
    (= model/tool-document-summary-result (:tool_name (first tool-calls))) "Save"
    :else "Confirm"))

(defn confirmation-message [{:keys [is-last
                                    tool-call-id-map
                                    set-loading-status-fn!
                                    *sidebar-open]
                             :as options}
                            message-obj]
  (with-let [*disabled (atom false)
             *status (atom nil)
             *confirm-actions (atom {})
             *tool-call-update-count (atom 0)
             ;; Cancel, and a tool that has to decline the prompt itself (#3085): the card
             ;; locks its buttons until the decision lands.
             deny! (fn []
                     (reset! *disabled true)
                     ;; The request runs the next model call, so the
                     ;; indicator must be on before it returns.
                     (set-loading-status-fn! true)
                     (controller/dispatch
                       :chat-messages
                       :deny
                       {:conversation-id (:chat_conversation_id message-obj)
                        :on-success (fn [_]
                                      (reset! *status model/tool-denied)
                                      (reset! *sidebar-open false))
                        :on-error (fn [err]
                                    (set-loading-status-fn! false)
                                    (reset! *disabled false)
                                    (throw err))}))]
    (util/disable-tool {:disabled @*disabled
                        :class :u-non-clickable
                        :component [:div
                                    (let [tool-call-ids (get-in message-obj [:meta :tool_call_ids])
                                          ;; Overlay user-edited :actual_arguments on top of the model's original
                                          ;; :arguments so the confirmation form displays edits made after a
                                          ;; pre-execute validation failure.
                                          tool-calls (mapv #(update % :arguments merge (:actual_arguments %))
                                                           (vals (select-keys tool-call-id-map tool-call-ids)))
                                          is-create-collection-tool (fn [tool-call]
                                                                      (= model/tool-create-collection (:tool_name tool-call)))
                                          create-collection-group (filter is-create-collection-tool tool-calls)
                                          num-tool-calls (count tool-calls)
                                          multiple (> num-tool-calls 1)
                                          status (or @*status (first (filter not-empty (map :status tool-calls))) model/tool-pending)
                                          tool-update-success-cb (fn []
                                                                   (swap! *tool-call-update-count inc)
                                                                   (when (= @*tool-call-update-count num-tool-calls)
                                                                     (reset! *disabled true)
                                                                     (controller/dispatch
                                                                      :chat-messages
                                                                      :confirm
                                                                      {:conversation-id (:chat_conversation_id message-obj)
                                                                       :on-success (fn [_] (reset! *status model/tool-confirmed))
                                                                       :on-error (fn [err]
                                                                                   (set-loading-status-fn! false)
                                                                                   (reset! *disabled false)
                                                                                   (reset! *tool-call-update-count 0)
                                                                                   (throw err))})))]
                                      [:div
                                       (condp = status
                                         model/tool-confirmed [tool-result {:type :confirm :title "Action confirmed"}]
                                         model/tool-denied [tool-result {:type :deny :title "Action denied"}]
                                         model/tool-confirmed-changed [tool-result {:type :confirm :title "Action confirmed with changes"}]
                                         (when is-last
                                           [:div.c-chat-tool-call-confirmation 
                                            (when (seq create-collection-group)
                                              (tool/create-collection-group (assoc options
                                                                                   :*confirm-actions *confirm-actions
                                                                                   :tool-update-success-cb tool-update-success-cb) create-collection-group message-obj)) 
                                            (doall
                                             (for [tc (remove is-create-collection-tool tool-calls)
                                                   :let [setup-confirm-action (fn [confirm-cb]
                                                                                (swap! *confirm-actions assoc (:id tc) confirm-cb))]]
                                               (if is-last
                                                 ^{:key (:id tc)}
                                                 [tool/tool (assoc options
                                                                   :register-confirm-action-fn setup-confirm-action
                                                                   :tool-update-success-cb tool-update-success-cb
                                                                   :deny-confirmation! deny!) message-obj tc])))
                                            [:footer.o-flex-align--space-between
                                             [:div]
                                             [:div
                                              [:button.c-button--wide.c-button--outlined {:on-click (util/handler deny!)}
                                               "Cancel"]

                                              " "
                                              [:button.c-button--wide {:on-click (util/handler
                                                                                   (fn []
                                                                                     (reset! *tool-call-update-count 0)
                                                                                     (set-loading-status-fn! true)
                                                                                     (doseq [[tc-id action] @*confirm-actions]
                                                                                       (action))))}
                                               (confirm-button-label tool-calls multiple)]]]]))])]})))

;; The id of the streamed reply. The delta events rewrite this one entry in
;; :messages, and the saved message replaces it. One component therefore renders
;; the text from the first word to the last.
(def streaming-message-id "streaming")

(defn- without-streaming-message [messages]
  (vec (remove #(= (:id %) streaming-message-id) messages)))

;; A message arrives twice when its tool calls run. The second carries the
;; status and arguments its confirmation card reads.
(defn- replace-message [messages message]
  (mapv #(if (= (:id %) (:id message)) message %) messages))

;; The server reads the stop flag and sends the text as two statements. A delta
;; of a stopped turn can therefore land after the cancel message. The gate drops
;; it by the turn id every streamed payload carries.
(defn- accept-payload? [state response]
  (model/accept-turn-payload? (:turn-seen state) (:stopped-turns state) (:turn response)))

(defn- note-turn [state response]
  (update state :turn-seen (fnil max 0) (or (:turn response) 0)))

(defn chat-message [{:keys [send-message-fn
                            tool-call-id-map
                            *selected-family-id
                            *sidebar-panels
                            set-loading-status-fn!
                            *sidebar-open
                            latest-reply-id
                            complete]}
                    {:keys [message message-obj sentTime sender direction
                            position files is-last error error-type]}]
  (with-let [disable-buttons (atom false)]
    (let [present? (or
                     (seq files)
                     (= (:type message-obj) "suggested_responses")
                     (= (:type message-obj) "confirmation")
                     (not-empty (:content message-obj)))
          md-node [:span
                   (when (seq files)
                     [message-files nil files])
                   (cond
                     (= (:type message-obj) "suggested_responses")
                     [suggested-responses-message {:is-last is-last
                                                   :send-message-fn send-message-fn}
                      message-obj]
                     ,
                     (= (:type message-obj) "confirmation")
                     [confirmation-message {:is-last is-last
                                            :tool-call-id-map tool-call-id-map
                                            :*selected-family-id *selected-family-id
                                            :*sidebar-panels *sidebar-panels
                                            :set-loading-status-fn! set-loading-status-fn!
                                            :*sidebar-open *sidebar-open} message-obj]
                     ,
                     :else [markdown-message nil message])
                   ;; A completed chat shows given ratings read-only; the server refuses changes too.
                   ;; THUMBS_ENABLE=false on the server hides them altogether.
                   (when (and (model/ratable? message-obj) (enabled "thumbs"))
                     [feedback/feedback-row message-obj {:latest (= (:id message-obj) latest-reply-id)
                                                         :read-only complete}])]

          model-js (clj->js {:message nil
                             :sender sender
                             :sentTime sentTime
                             :type "custom"
                             :direction direction
                             :position (or position "single")})]
      (when present?
        [:> Message
         {:model model-js
          :payload (r/as-element md-node)
          :className (cond
                       (model/is-intermediate message-obj) "c-string-field chat-message-intermediate"
                       (model/is-system-error message-obj) "cs-message--hidden"
                       (model/is-stopped message-obj) "cs-message--stopped"
                       ;; Send-failure styling only where the send really failed.
                       (and error (not (model/turn-ran-error-types error-type))) "c-string-field cs-message--send-error"
                       :else "c-string-field")
          :type "custom"}]))))

(defn upload-indicator [chat-state]
  (when (:uploading? @chat-state)
    [:div.upload-indicator "⏳ Uploading files..."]))

(defn hello []
  [:div.cs-hello-container
   [:h1.cs-hello-header (str "Hello, " (:first_name (api/get-auth-info :user)))]
   [:h2.cs-hello-subheader "How can I assist you today?"]
   [:div.cs-hello-box-container
    [:div.cs-hello-box
     [:img {:src "/static/img/icons/aark-upload-documents.png"}]
     [:h3.cs-hello-text "Upload new documents"]]
    [:div.cs-hello-box
     [:img {:src "/static/img/icons/aark-select-collections.png"}]
     [:h3.cs-hello-text "Select relevant collections"]]
    [:div.cs-hello-box
     [:img {:src "/static/img/icons/aark-ask-question.png"}]
     [:h3.cs-hello-text "Ask a question"]]]])

(defn sidebar [options panels *sidebar-open]
  (with-let [sidebar-open? (atom true)
             actions-open? (atom false)
             panels-open-map (atom nil)]
    (when @*sidebar-open
      (let [hide-chrome? (some (fn [[_ {config :config}]] (:hide-chrome config)) panels)]
        [:> Sidebar {:position "right"}
         (when-not hide-chrome?
           [:div.cs-sidebar-header
            [:a.cs-sidebar-close
             {:on-click #(reset! *sidebar-open false)} "✕"]])
         (doall
          (for [[panel-id {{:keys [title hide-chrome]} :config
                           component :component}] panels]
            ^{:key panel-id}
            [:div {:class (when hide-chrome "review-panel-wrapper")}
             [:> ExpansionPanel {:title title
                                 :open true}
              component]]))]))))

(defn- review-in-progress-flash
  "Explains why a chat with an unsaved review cannot be completed, linking back
  to that review unless it is already open (#3155). Rethrows any other error for
  the generic handler."
  [conversation-id current-conversation-id error-response]
  (let [{:keys [type message]} (get-in error-response [:response :body])]
    (if (= type "conversation_review_in_progress")
      (set-flash :error [:span message
                         (when (not= (str conversation-id) (str current-conversation-id))
                           [:<> " " [:a {:href (str "#/chat?conversation_id=" conversation-id)} "Open the review"]])])
      (throw error-response))))

(defn context-menu [conversation-id options chat-state]
  (let [{:keys [initial-conversation-name
                renaming-id
                rename-input-value
                context-menu-id
                current-conversation-id
                hide-complete]} options
        close (fn [_] (reset! context-menu-id nil))]
    (r/create-class
     {:reagent-render
      (fn []
        [:div.cs-context-menu {:on-click #(.stopPropagation %)}
         [:div.cs-context-menu__item {:on-click (fn [e]
                                                  (reset! renaming-id conversation-id)
                                                  (reset! rename-input-value initial-conversation-name)
                                                  (close e))} "Rename"]
         (when-not hide-complete
           [:div.cs-context-menu__item {:on-click (util/handler
                                                   (fn [e]
                                                     (controller/dispatch :conversation :complete {:conversation-id conversation-id
                                                                                                   :on-success (fn []
                                                                                                                 (swap! chat-state assoc :complete true)
                                                                                                                 (controller/dispatch :conversations :load)
                                                                                                                 (close e))
                                                                                                   :on-error (fn [error-response]
                                                                                                               (close e)
                                                                                                               (review-in-progress-flash conversation-id current-conversation-id error-response))})))} "Complete"])])
      :component-did-mount    (fn [_] (.addEventListener js/document "click" close))
      :component-will-unmount (fn [_] (.removeEventListener js/document "click" close))})))

(defn conversation-list-item [conversation options]
  (let [{:keys [renaming-id rename-input-value menu-open? context-menu-id rename-conversation current-conversation-id hide-complete]} options
        active-id (= (str (:id conversation)) current-conversation-id)
        is-renaming (= (:id conversation) @renaming-id)
        conversation-name (or (not-empty (:name conversation))
                              (not-empty (:summary conversation))
                              (util/ellipsis (:content conversation) 7))]
    [:div.cs-menu__inside
     {:class (when active-id "is-active")}
     (if is-renaming
       [:input.cs-rename-input
        {:type "text"
         :value @rename-input-value
         :on-change (fn [ev] (reset! rename-input-value (.. ev -target -value)))
         :on-key-down (fn [ev]
                        (when (= (.-key ev) "Enter")
                          (.preventDefault ev)
                          (rename-conversation (:id conversation) @rename-input-value)))
         :on-blur (fn [_]
                    (reset! renaming-id nil))
         :auto-focus true}]
       [:div.cs-menu__content-area
        {:on-click (fn []
                     (util/set-location (str "#/chat/?" (util/make-params
                                                         (util/remove-nil-values {:conversation_id (:id conversation)
                                                                                  :open_chats_expanded (when-not (:complete conversation) @(:expanded-atom options))
                                                                                  :completed_chats_expanded (when (:complete conversation) @(:expanded-atom options))})))))}
        conversation-name])

     (when (and active-id (not is-renaming))
       [:div.cs-menu__options-wrapper
        [:div.cs-menu__options-btn
         {:on-click (fn [e]
                      (let [btn-el (.-currentTarget e)
                            rect (.. btn-el getBoundingClientRect)
                            container-el (js/document.querySelector ".cs-menu-content")
                            container-rect (if container-el (.getBoundingClientRect container-el) {:top 0 :left 0})
                            top-offset (- (.-top rect) (.-top container-rect))
                            left-offset (- (.-left rect) (.-left container-rect))
                            height (.-height rect)
                            scroll-top (if container-el (.-scrollTop container-el) 0)]

                        (reset! (:context-menu-position-atom options)
                                {:top (+ top-offset scroll-top)
                                 :left left-offset
                                 :height height}))
                      (.stopPropagation e)
                      (swap! context-menu-id
                             #(if (= % (:id conversation)) nil (:id conversation))))} "⋮"]])]))

(defn conversation-section [conversations options]
  (let [list-items (doall
                    (for [conversation conversations]
                      ^{:key (:id conversation)}
                      [conversation-list-item conversation options]))
        is-expanded @(:expanded-atom options)
        needs-more? (> (count conversations) 7)]

    [:div.cs-conv-section
     [:div.cs-chats-label (:title options)]
     [:div.cs-menu-ongoing-chats
      {:class (when is-expanded "is-expanded")}
      list-items]
     (when (and needs-more? (not is-expanded))
       [:div.cs-show-more-btn
        {:on-click #(swap! (:expanded-atom options) not)}
        "Show more..."])
     (when (and needs-more? is-expanded)
       [:div.cs-show-more-btn
        {:on-click #(swap! (:expanded-atom options) not)}
        "Show less..."])]))

(defn conversations-menu [options chat-state]
  (let [menu-open? (:menu-open? options)
        open-chats-expanded (atom (:open-chats-expanded options))
        completed-chats-expanded (atom (:completed-chats-expanded options))]
    (fn []
      (let [open-conversations (filter #(not (:complete %)) @(:conversations options))
            complete-conversations (->> (filter :complete @(:conversations options))
                                        (sort-by :complete_at)
                                        (reverse))
            active-context-id @(:context-menu-id options)
            active-position @(:context-menu-position-atom options)]
        [:div.cs-menu {:class (when @menu-open? "open")}
         [:div.cs-menu-content
          [:span.cs-menu-toggle {:on-click #(swap! menu-open? not)} "☰" [:span.cs-menu-chevron "‹"]]
          [:div.cs-new-chat {:on-click (fn []
                                         (reset! chat-state new-chat)
                                         (util/set-location "#/chat"))} [:span.cs-green-ring "+"] "New chat"]

          [conversation-section open-conversations (merge {:title "Open chats"
                                                           :expanded-atom open-chats-expanded} options)]

          [conversation-section complete-conversations (merge {:title "Completed chats"
                                                               :expanded-atom completed-chats-expanded
                                                               :hide-complete true} options)]
          (when (and active-context-id active-position)
            (let [active-conv (first (filter #(= (:id %) active-context-id) @(:conversations options)))
                  active-name (or (not-empty (:name active-conv))
                                  (not-empty (:summary active-conv))
                                  (util/ellipsis (:content active-conv) 7))
                  is-complete (:complete active-conv)]

              [:div.cs-context-menu-wrapper
               {:style {:position "absolute"
                        :top (+ (:top active-position) (:height active-position) 20)
                        :left  (- (:left active-position) 90)
                        :z-index 2000}}
               [context-menu active-context-id (merge
                                                (select-keys options [:renaming-id :rename-input-value :context-menu-id :current-conversation-id])
                                                {:initial-conversation-name active-name
                                                 :hide-complete is-complete}) chat-state]]))]]))))

(defn modal-file-item [options file]
  [:div.cs-modal-file-item__container
   [:span.cs-modal-file-item__icon
    (cond
      (.startsWith (:type file) "image/") "🖼️"
      (.includes (:type file) "pdf") "📄"
      (.includes (:type file) "word") "📝"
      (.includes (:type file) "excel") "📊"
      :else "📎")]
   [:div.cs-modal-file-item__info
    [:div.cs-modal-file-item__name (:name file)]
    [:div.cs-modal-file-item__size (str (.toFixed (/ (:size file) 1024) 1) " KB")]]
   (when (:remove-file-fn options)
     [:span.cs-modal-file-item__remove-btn
      {:on-click #((:remove-file-fn options) (:upload-id file))} "×"])])


(defn attached-files-modal [chat-state]
  (let [remove-file-fn (fn [upload-id]
                         (swap! chat-state update :attached-files
                                (fn [files]
                                  (remove #(= (:upload-id %) upload-id) files))))]

    [:div.cs-attached-files-modal
     [:h2 "Uploaded documents"]
     [:div.cs-modal-file-list__wrapper
      [:div.cs-modal-file-list__list
       (for [file (:attached-files @chat-state)]
         ^{:key (:id file)}
         [modal-file-item {:remove-file-fn remove-file-fn} file])]]
     [:div.cs-modal-footer
      [:button.cs-modal-close-btn
       {:on-click (util/handler (fn []
                                  (swap! state/modals update :chat-attached-files :enabled :false)))} "Close"]]]))

(defn- collection-chip [{:keys [assignment disabled? on-remove measuring?]}]
  (let [label (get-in assignment [:collection-obj :plainLabel])]
    [:span.cs-selected-collection {:title label}
     [:span.cs-selected-collection-name label]
     [:span.cs-selected-collection-remove
      (merge {:disabled disabled?}
             (when (and (not disabled?) (not measuring?))
               {:on-click #(on-remove assignment)}))
      "×"]]))

(defn- collection-count-chip [{:keys [total hidden disabled? on-click measuring?]}]
  [:button.cs-selected-collection.cs-collection-count
   (merge {:type "button"
           :aria-label (str "Show all " total " selected collections")}
          (if measuring?
            {:tab-index -1}
            (merge {:disabled disabled?}
                   (when-not disabled? {:on-click on-click}))))
   (str "+" hidden " more")])

(defn- selected-collections-row
  "One row of selected-collection chips (#3013). Shows as many chips as fit;
  the remainder collapses into a count chip that opens the collection modal."
  [_opts]
  (let [visible-count (atom nil)
        container-el (clojure.core/atom nil)
        measure-el (clojure.core/atom nil)
        resize-observer (clojure.core/atom nil)
        measure! (fn []
                   (when-let [container @container-el]
                     (when-let [measure @measure-el]
                       (let [rect-w (fn [el] (.-width (.getBoundingClientRect el)))
                             children (array-seq (.-children measure))
                             widths (mapv rect-w (butlast children))
                             counter-w (rect-w (last children))
                             gap (let [g (js/parseFloat (.-columnGap (js/getComputedStyle container)))]
                                   (if (js/isNaN g) 8 g))
                             avail (rect-w container)
                             total (count widths)
                             all-w (+ (reduce + widths) (* gap (dec total)))
                             prefix (vec (reductions + widths))
                             ;; gap count for n chips + counter is n: (n-1) between chips, 1 before counter
                             fits? (fn [n] (<= (+ (nth prefix (dec n)) (* gap n) counter-w) avail))
                             n (if (<= all-w avail)
                                 total
                                 (or (last (filter fits? (range 1 total))) 1))]
                         (when (not= @visible-count n)
                           (reset! visible-count n))))))]
    (r/create-class
     {:display-name "selected-collections-row"
      :component-did-mount (fn [_]
                             (let [ro (js/ResizeObserver. (fn [_] (measure!)))]
                               (reset! resize-observer ro)
                               (when-let [el @container-el]
                                 (.observe ro el))))
      ;; The chat view re-renders on every keystroke; measuring reads layout
      ;; (forced reflow), so only re-measure when the chips themselves changed.
      ;; Width changes are covered by the ResizeObserver.
      :component-did-update (fn [this [_ old-opts]]
                              (when (not= (:collections old-opts)
                                          (:collections (second (r/argv this))))
                                (measure!)))
      :component-will-unmount (fn [& _]
                                (some-> @resize-observer .disconnect)
                                (reset! resize-observer nil))
      :reagent-render
      (fn [{:keys [collections disabled? on-remove on-show-all]}]
        (let [total (count collections)
              n (min (or @visible-count total) total)
              hidden (- total n)]
          [:span.cs-selected-collections-container {:ref #(reset! container-el %)}
           ;; Mirror of all chips at natural width, invisible; measure! reads it
           ;; to decide how many chips fit next to the count chip.
           [:span.cs-collections-measure {:aria-hidden true
                                          :ref #(reset! measure-el %)}
            (doall
             (for [assignment collections]
               ^{:key (:collection assignment)}
               [collection-chip {:assignment assignment :disabled? disabled? :measuring? true}]))
            [collection-count-chip {:total total :hidden (max 1 (dec total)) :measuring? true}]]
           (doall
            (for [assignment (take n collections)]
              ^{:key (:collection assignment)}
              [collection-chip {:assignment assignment
                                :disabled? disabled?
                                :on-remove on-remove}]))
           (when (pos? hidden)
             [collection-count-chip {:total total
                                     :hidden hidden
                                     :disabled? disabled?
                                     :on-click on-show-all}])]))})))

(defn chat-view [{:keys [initialized-chat]
                  :as options} chat-state]
  (let [message (atom  (get-in @chat-state [:input-content (:conversation-id options)]))
        conversations (subscribe :conversations)
        on-attach-click (fn []
                          (let [input (js/document.createElement "input")]
                            (set! (.-type input) "file")
                            (set! (.-multiple input) true)
                            (set! (.-accept input) "image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx")
                            (set! (.-onchange input)
                                  (fn [e]
                                    (let [files (.-files (.-target e))]
                                      (doseq [i (range (.-length files))]
                                        (upload-file chat-state (.item files i))))))
                            (.click input)))
        menu-open? (atom (:menu-open? options))
        context-menu-id (atom nil)
        context-menu-position-atom (atom nil)
        renaming-id (atom nil)
        rename-input-value (atom "")
        rename-conversation (fn [conversation-id new-value]
                              (controller/dispatch :conversation
                                                   :rename {:conversation-id conversation-id
                                                            :conversation-name new-value
                                                            :on-success (fn []
                                                                          (controller/dispatch :conversations
                                                                                               :load {:on-success (fn []
                                                                                                                    (reset! renaming-id nil)
                                                                                                                    (reset! rename-input-value ""))}))}))
        is-complete-conversation? (fn [conversation-id]
                                    (:complete (first (filter #(= (str (:id %)) conversation-id) @conversations))))
        families (subscribe :permitted-groups)
        include-inactive-collections (atom false)
        primary-collection-id-global (atom nil)
        open-collections-modal (clojure.core/atom nil)
        selected-family-id (l/derive (l/key :selected-family-id) chat-state)
        assignments (l/derive (l/key :assignments) chat-state)
        sidebar-panels (atom nil)
        sidebar-open (atom false)
        update-assignments (fn update-assignments
                             ([selected-family-id assignments]
                              (update-assignments selected-family-id assignments nil))
                             ([selected-family-id assignments callbacks]
                              (controller/dispatch :conversation
                                                   :update-assignments (merge {:conversation-id (or (:conversation-id options) (:current-conversation-id @chat-state))
                                                                               :family-id selected-family-id
                                                                               :collection-ids (vec (assignments-to-id-set assignments))}
                                                                              (select-keys callbacks [:on-success :on-error])))))
        stop (fn []
               (let [last-user-message (last (filter #(= (:sender %) model/user) (:messages @chat-state)))]
                 (when last-user-message
                   (swap! chat-state assoc :stop true)
                   (controller/dispatch :chat-stop
                                        :load {:message-id (:id last-user-message)
                                               :on-success (fn []
                                                             (swap! chat-state
                                                                    (fn [state]
                                                                      (-> state
                                                                          (update :messages (fn [messages]
                                                                                              (mapv (fn [message]
                                                                                                      (if (= (:id message) (:id last-user-message))
                                                                                                        (assoc message :stopped true)
                                                                                                        message)) messages)))
                                                                          (update :stopped-turns (fnil conj #{}) (:id last-user-message))
                                                                          (update :messages without-streaming-message)
                                                                          (assoc :streaming-text nil)
                                                                          (assoc :turn-state nil)
                                                                          (assoc :loading? false)
                                                                          (assoc :stop false)))))
                                               :on-error (fn [error]
                                                           (swap! chat-state
                                                                  (fn [state]
                                                                    (-> state
                                                                        (assoc :stop false)
                                                                        (assoc :loading? false))))
                                                           (throw error))}))))]
    (controller/dispatch :conversations :load)
    (fn []
      (when-let [text (:restore-input @chat-state)]
        (reset! message text)
        (swap! chat-state assoc :restore-input nil))
      (when-let [files (:restore-files @chat-state)]
        (swap! chat-state (fn [s] (-> s (assoc :attached-files files) (assoc :restore-files nil)))))
      (let [is-multi-subscriber? (> (count (distinct (map :subscriber_id @families))) 1)
            requires-family-selection? (and is-multi-subscriber? (nil? (:selected-family-id @chat-state)))
            outage-disabled? (contains? #{"outage" "network"} (:status @aark-status))
            unrecoverable-error? (= :unrecoverable (get-in @chat-state [:input-error :type]))
            can-send-message? (not
                               (or
                                outage-disabled?
                                ;; A message sent before the seeded context lands
                                ;; would create the conversation unscoped.
                                (:seeding-context? @chat-state)
                                requires-family-selection?
                                unrecoverable-error?
                                (and (str/blank? @message)
                                     (empty? (:attached-files @chat-state)))
                                (:loading? @chat-state)
                                (:renewing-token? @chat-state)
                                (model/awaits-confirmation @chat-state)
                                (is-complete-conversation? (:conversation-id options))))
            family-locked? (model/family-locked? @chat-state is-multi-subscriber?)
            selected-collections (set  (filterv (fn [assignment] (contains? assignment :collection)) @assignments))
            tool-call-id-map (util/associate-by :id
                               (flatten
                                 (doall (for [msg (:all-messages @chat-state)
                                              :let [tool-calls (get-in  msg [:message-obj :tool_calls])]
                                              :when (seq tool-calls)]
                                          tool-calls))))]
        [:div.cs-chat-app
         [conversations-menu {:conversations conversations
                              :menu-open? menu-open?
                              :open-chats-expanded (:open_chats_expanded options)
                              :completed-chats-expanded (:completed_chats_expanded options)
                              :context-menu-id context-menu-id
                              :context-menu-position-atom context-menu-position-atom
                              :renaming-id renaming-id
                              :rename-input-value rename-input-value
                              :rename-conversation rename-conversation
                              :current-conversation-id (or (:conversation-id options) (:current-conversation-id @chat-state))} chat-state]

         [:div.cs-main-content
          [:div.cs-header
           (when-not @menu-open?
             [:span.cs-menu-toggle {:on-click #(swap! menu-open? not)} "☰"])
           [:div.cs-header-inner
            [:div.cs-header-left
             [:span.cs-family {:class (when (> (count @families) 1) "has-chevron")}
              [:select {:disabled (or (:complete @chat-state) family-locked?)
                        :title (when family-locked? "Start a new chat to work with another family")
                        :on-change (fn [ev]
                                     (let [id (.-value (.-target ev))
                                           family (some #(when (= (str (:id %)) id) %) @families)
                                           previous-family-id @selected-family-id]
                                       (swap! chat-state assoc :selected-family-id (:id family))
                                       (when (:current-conversation-id @chat-state)
                                         (initialized-chat
                                          (fn []
                                            (swap! chat-state assoc :family-change-pending? true)
                                            (update-assignments
                                             @selected-family-id @assignments
                                             {:on-success (fn [_] (swap! chat-state assoc :family-change-pending? false))
                                              :on-error (fn [error]
                                                          (swap! chat-state assoc
                                                                 :selected-family-id previous-family-id
                                                                 :family-change-pending? false)
                                                          (throw error))}))))))}
               [:option {:value "<CHOOSE>"} "Select family"]
               (doall
                (for [family @families]
                  ^{:key (:id family)}
                  [:option
                   (set-selected @selected-family-id (:id family) {:value (:id family) :key (:id family)})
                   (:name family)]))]]
             (when (not (empty? selected-collections))
               [:<>
                [:span.cs-selected-collections-label "Selected collections:"]
                [selected-collections-row
                 {:collections (vec selected-collections)
                  :disabled? (:complete @chat-state)
                  :on-remove (fn [assignment]
                               (swap! assignments
                                      (fn [all-assignments]
                                        (filterv #(not= (:collection %) (:collection assignment)) all-assignments)))
                               (update-assignments @selected-family-id @assignments))
                  :on-show-all (fn [] (some-> @open-collections-modal (apply [])))}]])]
            [:div.cs-header-right
             (when (and @selected-family-id (not (:complete @chat-state))) 
               [assignments-edit-modal
                assignments
                selected-family-id
                primary-collection-id-global
                {:include-inactive-atom include-inactive-collections
                 :select-from-tree-label "Select collection"
                 :expose-open-fn open-collections-modal
                 :save-cb (fn [_]
                            (initialized-chat #(update-assignments @selected-family-id @assignments)))
                 :bt-class "__outlined"}])
             (when (:current-conversation-id @chat-state)
               (let [downloadImg [:img {:src "/static/img/icons/download.svg" :title "Download debug file"}]
                     has-llm-message? (some #(= "llm" (:sender %)) (:messages @chat-state))]
                 (if has-llm-message?
                   [common/download-button {:ajax-url (str "/chat/debug/" (:current-conversation-id @chat-state) "/")
                                            :ajax-options {:params {:download "1"}
                                                           :response-format (ajax.core/ring-response-format {:format (text-response-format)})}
                                            :component :a}
                    downloadImg]
                   [:div {:style {:display :inline-block}} downloadImg])))]]]
          [sidebar options @sidebar-panels sidebar-open]
          [:div.cs-chat-body
           [error-message chat-state]
           [upload-indicator chat-state]
           [:div.cs-chat-inner
            [:> (dropzone-overlay chat-state)]
            [:div.cs-main-container
             [:div.cs-chat-container {:ref (fn [el]
                                             (when el
                                               (doseq [a (sel [:div.cs-message__content "a"])]
                                                 (let [ancestors (dommy/ancestors a)]
                                                   (when-not (some #(dommy/has-class? % "c-chat-tool-call-confirmation") ancestors)
                                                     (dommy/set-attr! a :target "_blank")
                                                     (dommy/add-class! a "cs-link-icon"))))))}
              [:div.cs-outage-banner-wrapper [aark-outage-banner]]
              [:> MessageList
               ;; The text bubble is the signal while text streams. Once the
               ;; model moves on to a tool, the indicator returns under it.
               {:typingIndicator (when (and (:loading? @chat-state)
                                           (or (str/blank? (:streaming-text @chat-state))
                                               (not (contains? #{nil "writing"} (:turn-state @chat-state)))))
                                   (react/createElement
                                    TypingIndicator
                                    #js {:content (model/turn-state-label @chat-state)}))}
               (when (zero? (count (:messages @chat-state))) [hello])
               (let [last-index (dec (count (:messages @chat-state)))
                     ;; From the end: the newest reply is near it, and this runs on every streamed chunk.
                     latest-reply-id (some #(when (model/ratable? (:message-obj %)) (:id %))
                                           (rseq (vec (:messages @chat-state))))]
                 (for [[index msg] (map-indexed vector (:messages @chat-state))]
                   ^{:key (:id msg)}
                   [chat-message
                    {:send-message-fn (fn [text]
                                        (reset! message text)
                                        (initialized-chat #(message-send (merge {:conversations @conversations} options) chat-state message) true))
                     :tool-call-id-map tool-call-id-map
                     :*selected-family-id selected-family-id
                     :*sidebar-panels sidebar-panels
                     :set-loading-status-fn! (fn [status] (swap! chat-state assoc :loading? status))
                     :*sidebar-open sidebar-open
                     :latest-reply-id latest-reply-id
                     :complete (:complete @chat-state)}
                    (assoc
                     msg
                     :is-last (= index last-index))]))]
              [:div.cs-input-container
               [chat-input-banner chat-state]
               [:div.cs-tom-message-input
                [:div.input-wrapper
                 [:textarea.message-field
                  {:placeholder "Ask me anything..."
                   :value @message
                   :disabled (when (or (:loading? @chat-state) (is-complete-conversation? (:conversation-id options))) "disabled")
                   :on-change (fn [ev]
                                (reset! message (.. ev -target -value))
                                (when (:current-conversation-id @chat-state)
                                  (swap! chat-state update-in [:input-content] assoc (:current-conversation-id @chat-state) @message)))
                   :on-key-down (fn [ev]
                                  (when (and (= (.-key ev) "Enter")
                                             (not (.-shiftKey ev)))
                                    (.preventDefault ev)
                                    (when can-send-message?
                                      (initialized-chat #(message-send (merge {:conversations @conversations} options) chat-state message) true))))}]

                 [:a.attach-href (when-not (:complete @chat-state)
                                   {:on-click on-attach-click}) "+"]
                 (when (> (count (:attached-files @chat-state)) 0)
                   [:div.attached-files {:on-click (util/handler
                                                    (fn []
                                                      (common/show-modal :chat-attached-files
                                                                         [attached-files-modal chat-state]
                                                                         {:modal-class "cs-modal-attached-files"
                                                                          :close-disable true})))}
                    [:div [:img {:src "/static/img/icons/aark-attached-files.png"}]]
                    [:div.attached-files-counter (count (:attached-files @chat-state))]])
                 (if (and (:loading? @chat-state)
                          (not (model/stop-guarded? @chat-state)))
                   [:button.stop-btn (merge
                                      {:title "Stop"
                                       :aria-label "Stop"
                                       :on-click (fn []
                                                   (stop))}
                                      (when (:stop @chat-state)
                                        {:disabled "disabled"})) "⏹"]
                   [:button.send-btn (merge
                                      {:on-click (fn []
                                                   (initialized-chat #(message-send (merge {:conversations @conversations} options) chat-state message) true))}
                                      (when requires-family-selection?
                                        {:title "Please select a family before sending messages"})
                                      (when-not can-send-message?
                                        {:disabled "disabled"}))
                    ;; Same arrow as the chat panel send button — the unicode
                    ;; glyph renders inconsistently across browsers.
                    [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 24 24"
                           :width "14" :height "14" :fill "white"}
                     [:path {:d "M12 4l-8 8h5v8h6v-8h5z"}]]])]]
               [:div.cs-mistakes-info
                [:span.cs-brand "AARK"] " can make mistakes. Please double-check responses."]]]]]]]]))))

(def new-chat {:messages []
               :turn-seen 0
               :stopped-turns #{}
               :token ""
               :loading? false
               :error nil
               :input-error nil
               :pending-message nil
               :pending-files nil
               :restore-input nil
               :restore-files nil
               :attached-files []
               :uploading? false
               :current-conversation-id nil
               :input-content {}
               :assignments []
               :selected-family-id nil
               :seeding-context? false
               :family-change-pending? false
               :complete false
               :stop false})

(defn chat [query-params]
  (with-let [chat-state (atom new-chat)
             permitted-groups (subscribe :permitted-groups)
             event-source (atom nil)
             ;; A reconnect replaces event-source, and its timer can fire after the chat is
             ;; gone; unmount closes the current stream and cancels the timer (#3085).
             reconnect-timer (atom nil)
             unmounted? (atom false)
             retry-count (atom 0)
             conversation-id (:conversation_id query-params)
             ;; A collection named in the URL opens a new conversation already
             ;; scoped to it, the way the collection panel links into AARK.
             context-collection-id (:collection_id query-params)
             load-chat-messages (fn [id on-error & [background?]]
                                  (controller/dispatch :chat-messages :load {:conversation-id id
                                                                             :background? background?
                                                                             :on-success (fn [chat-data]
                                                                                           (let [all-messages     (mapv model/chat-message-to-ui-message (:messages chat-data))
                                                                                                 all-messages     (model/mark-historical-errors all-messages)
                                                                                                 last-user-msg    (last (filter #(= (:sender %) model/user) all-messages))
                                                                                                 last-has-error?  (:error last-user-msg)
                                                                                                 last-error-type  (:error-type last-user-msg)
                                                                                                 delivered?       (model/no-resend-error-types last-error-type)]
                                                                                            (swap! chat-state
                                                                                             (fn [state]
                                                                                               (-> state
                                                                                                 (assoc :all-messages all-messages)
                                                                                                 (assoc :turn-seen 0 :stopped-turns #{})
                                                                                                 (assoc :messages (vec (remove :is-intermediate all-messages)))
                                                                                                 (assoc :loading? (let [last-message (last (:messages chat-data))
                                                                                                                        last-user-message (last (filter  #(= (:sender %) model/user) (:messages chat-data)))]
                                                                                                                    (if (:stopped last-user-message)
                                                                                                                      false
                                                                                                                      (or
                                                                                                                       (not (contains? #{model/llm model/system} (:sender last-message)))
                                                                                                                       (seq (:tool_calls last-message))
                                                                                                                       (model/decision-awaits-reply? (:messages chat-data))))))
                                                                                                 (assoc :selected-family-id (:collection_id (first (filter :is_family (:conversation_collections chat-data)))))
                                                                                                 (assoc :assignments (get-initial-ui-assignments (vec (remove :is_family (:conversation_collections chat-data)))))
                                                                                                 ;; Nothing of a delivered message is offered back for
                                                                                                 ;; resending, text and attachments alike.
                                                                                                 (cond-> last-has-error? (assoc :input-error {:type last-error-type})
                                                                                                         (and last-has-error? (not delivered?))
                                                                                                         (assoc :restore-input (:message last-user-msg))
                                                                                                         (and last-has-error? (not delivered?) (seq (:files last-user-msg)))
                                                                                                         (assoc :restore-files (mapv #(assoc % :upload-id (:id %)) (:files last-user-msg)))))))))
                                                                             :on-error on-error}))
             store-token! (fn [token]
                            (swap! chat-state assoc :token token :token-renew-at (+ (js/Date.now) model/token-renew-ms)))
             ;; Without a timeout, a request lost on a half-open connection after a wake-up
             ;; would keep Send disabled.
             renew-token (fn [id]
                           (p/map :token (model/chat-init id {:disable-loadin-counter true
                                                               :timeout model/token-renew-timeout-ms})))]
    (letfn [(sse-listener-init [token]
              (let [url (str js/API_BASE_URL "/chat/listen?token=" token "&stream=1")
                    max-delay-ms 30000
                    stable-timeout-ms 10000
                    reload-conversion-list (fn []
                                             (when (and (= (count (:messages @chat-state)) 2)
                                                        (= (:sender (last (:messages @chat-state))) model/llm))
                                               (controller/dispatch :conversations :load)))]

                (when (not (nil? @event-source))
                  (.close @event-source))
                (reset! event-source (js/EventSource. url))
                (swap! chat-state assoc :error nil :input-error nil)

                (set! (.-onmessage @event-source)
                      (fn [event]
                        (let [response (js->clj (js/JSON.parse (.-data event)) :keywordize-keys true)
                              message (:message response)]
                          (when (= (:type response) "delta")
                            (swap! chat-state
                                   (fn [state]
                                     (if-not (accept-payload? state response)
                                       state
                                       (let [text (str (:streaming-text state) (:text response))]
                                         (assoc (note-turn state response)
                                                :streaming-text text
                                                :messages (conj (without-streaming-message (:messages state))
                                                                {:direction "incoming"
                                                                 :sender model/llm
                                                                 :message text
                                                                 :message-obj {:content text}
                                                                 :id streaming-message-id
                                                                 :sentTime (js/Date.)})))))))
                          ;; A new model call starts here. Its text replaces what
                          ;; the browser holds from the call before.
                          (when (= (:type response) "reply-start")
                            (swap! chat-state
                                   (fn [state]
                                     (if-not (accept-payload? state response)
                                       state
                                       (let [base (model/step-base-after-reply-start (:turn-seen state) (:turn-step state) (:turn response))]
                                         (-> (note-turn state response)
                                             (assoc :streaming-text nil)
                                             (assoc :turn-state "thinking" :turn-step base :turn-step-base base
                                                    :turn-failed? false :turn-elapsed 0)
                                             (update :messages without-streaming-message)))))))
                          ;; The server names the phase and the seconds spent in it, and repeats a
                          ;; silent state with the count.
                          (when (= (:type response) "state")
                            (swap! chat-state
                                   (fn [state]
                                     (if-not (accept-payload? state response)
                                       state
                                       (assoc (note-turn state response)
                                              :turn-state (:state response)
                                              :turn-step (+ (or (:turn-step-base state) 0) (or (:step response) 0))
                                              :turn-failed? (boolean (:failed response))
                                              :turn-elapsed (or (:elapsed response) 0))))))
                          (when-not (contains? #{"keepalive" "delta" "reply-start" "state"} (:type response))
                            ;; The second arrival leaves the streamed text under it
                            ;; alone. That text belongs to the next model call,
                            ;; which already runs.
                            (let [known? (boolean (some #(= (:id %) (:id message)) (:messages @chat-state)))]
                              (swap! chat-state
                                     (fn [state]
                                       (let [state (if known?
                                                     state
                                                     (update state :messages without-streaming-message))
                                             state (if (or
                                                         (not-empty
                                                             (:content message))
                                                         (seq (:tool_calls message))
                                                         (= model/system (:sender message)))
                                                     (let [new-message {:direction "incoming"
                                                                        :sender (:sender message)
                                                                        :message (:content message)
                                                                        :id (:id message)
                                                                        :message-obj message
                                                                        :sentTime (js/Date.)}]
                                                       (if known?
                                                         (-> state
                                                             (update :messages replace-message new-message)
                                                             (update :all-messages replace-message new-message))
                                                         (-> state
                                                             (update :messages (fn [messages msg]
                                                                                 (let [last-message (last messages)
                                                                                       last-intermediate? (model/is-intermediate (:message-obj last-message))]
                                                                                   (if last-intermediate?
                                                                                     (conj (vec (butlast messages)) msg)
                                                                                     (conj messages msg))))
                                                                     new-message)
                                                             (update :all-messages conj new-message))))
                                                     state)
                                             last-user-message (last (filter  #(= (:sender %) model/user) (:messages state)))
                                             still-loading?    (and (not (:stopped last-user-message))
                                                                     (seq (:tool_calls message)))]
                                         (cond-> (assoc state
                                                        :loading? still-loading?
                                                        :error nil)
                                           (not known?)
                                           (assoc :streaming-text nil)
                                           (not still-loading?)
                                           (assoc :turn-state nil)))))
                              (when-not known?
                                (reload-conversion-list)
                                (when (model/is-system-error message)
                                  (swap! chat-state model/fail-turn message))))))
                        (js/setTimeout
                         (fn []
                           (when (= @retry-count 0)
                             nil)
                           (when (= (.-readyState @event-source) js/EventSource.OPEN)
                             (warn "Connection stable — resetting retry counter")
                             (reset! retry-count 0)
                             ;; Only this stream sets "network". Waiting for the notify stream's "ok"
                             ;; can leave the input blocked after the chat has recovered.
                             (when (= "network" (:status @aark-status))
                               (reset! aark-status nil))))
                         stable-timeout-ms)))

                (set! (.-onerror @event-source)
                      (fn [err]
                        (util/log err)
                        (warn "Chat connection error")
                        (.close @event-source)
                        (swap! chat-state assoc :loading? false :turn-state nil)
                        (when (> @retry-count 0)
                          (when @resolved-timeout
                            (js/clearTimeout @resolved-timeout)
                            (reset! resolved-timeout nil))
                          (reset! aark-status {:status "network" :retry-count @retry-count}))
                        (let [next-retry (min max-delay-ms
                                              (* 1000 (js/Math.pow 1.1 @retry-count)))]
                          (warn (str "Attempting to reconnect in "
                                     (/ next-retry 1000)
                                     "s (retry " (inc @retry-count) ")..."))
                          (reset! reconnect-timer
                                  (js/setTimeout
                                   (fn []
                                     (when-not @unmounted?
                                       (swap! retry-count inc)
                                       ;; The id is not the URL's: a chat begun here has none (#3191).
                                       (if-let [id (:current-conversation-id @chat-state)]
                                         ;; The user can open another chat while the renewal runs.
                                         (let [current? #(and (not @unmounted?)
                                                              (model/same-conversation? id @chat-state))
                                               resume (fn [stream-token]
                                                        (when (current?)
                                                          (sse-listener-init stream-token)
                                                          ;; Postgres queues nothing for a closed listener, so the
                                                          ;; events of the gap are gone. The saved rows are the truth.
                                                          (load-chat-messages id #(warn "Chat history reload failed"
                                                                                        (:status %))
                                                                              true)))]
                                           ;; The token can have expired while the tab was idle. If no new
                                           ;; one comes, the old one gets its chance, and a failure of the
                                           ;; stream continues the backoff.
                                           (.then (renew-token id)
                                                  (fn [new-token]
                                                    (when (current?) (store-token! new-token))
                                                    (resume new-token))
                                                  ;; A send can have renewed the token since this stream opened.
                                                  (fn [_] (resume (:token @chat-state)))))
                                         (sse-listener-init (:token @chat-state)))))
                                   next-retry)))))

                @event-source))]
      (let [load-failed #(show-chat-error! chat-state "Load conversation" % false)
            chat-init (fn [conversation-id conversation-created-cb]
                        (controller/dispatch :chat-init :load {:conversation-id conversation-id
                                                               :on-success (fn [response]
                                                                             (swap! chat-state assoc :initialized true)
                                                                             (store-token! (:token response))
                                                                             (swap! chat-state
                                                                                    (fn [state]
                                                                                      (-> state
                                                                                          (assoc :loading? false)
                                                                                          (assoc :complete (get-in response [:conversation :complete]))
                                                                                          (assoc :current-conversation-id (or conversation-id (get-in response [:conversation :id]))))))
                                                                             (if (nil? conversation-id)
                                                                               (swap! chat-state assoc :messages [])
                                                                               (load-chat-messages conversation-id load-failed))
                                                                             (sse-listener-init (:token response))
                                                                             (if (and (not conversation-id) (:selected-family-id @chat-state))
                                                                               (controller/dispatch :conversation
                                                                                 :update-assignments {:conversation-id (:current-conversation-id @chat-state)
                                                                                                      :family-id (:selected-family-id @chat-state)
                                                                                                      :collection-ids (vec (assignments-to-id-set (:assignments @chat-state)))
                                                                                                      :on-success (fn []
                                                                                                                    (conversation-created-cb (:current-conversation-id @chat-state)))})
                                                                               (conversation-created-cb (:current-conversation-id @chat-state))))
                                                               :on-error (fn [response]
                                                                           (when-not @unmounted?
                                                                             (show-chat-error! chat-state "Chat init" response
                                                                                               (some? conversation-id))))}))
            initialized-chat (fn [cb & [send?]]
                               (let [id (:current-conversation-id @chat-state)]
                                 (case (model/chat-step @chat-state send? (js/Date.now))
                                   :init (chat-init conversation-id cb)
                                   :drop nil
                                   :run (cb id)
                                   ;; A live stream never renews the token, so a send after a day open
                                   ;; would carry an expired one.
                                   :renew (let [proceed (fn [& _]
                                                          (swap! chat-state assoc :renewing-token? false)
                                                          (when (and (not @unmounted?)
                                                                     (model/same-conversation? id @chat-state))
                                                            (cb id)))]
                                            (swap! chat-state assoc :renewing-token? true)
                                            ;; Without a new token the send goes anyway and reports its own failure.
                                            (.then (renew-token id)
                                                   (fn [new-token]
                                                     (when (model/same-conversation? id @chat-state)
                                                       (store-token! new-token))
                                                     (proceed))
                                                   proceed)))))
            ;; The conversation does not exist yet, so the context is seeded into
            ;; the chat state the collection picker writes to; it reaches the
            ;; server with the first message.
            ;; The lone family a user has needs no picking, seeded chat or not.
            default-family! (fn []
                              (when (and (nil? (:selected-family-id @chat-state))
                                         (= 1 (count @permitted-groups)))
                                (swap! chat-state assoc :selected-family-id (:id (first @permitted-groups)))))
            finish-seeding (fn []
                             (swap! chat-state assoc :seeding-context? false)
                             (default-family!))
            ;; Scopes a not-yet-created conversation the way the picker would, so
            ;; a collection that cannot be read leaves an ordinary chat behind.
            seed-context-collection (fn [collection-id]
                                      (swap! chat-state assoc :seeding-context? true)
                                      (->> (collection-model/collection-details collection-id)
                                           (p/map (fn [collection]
                                                    (let [family-id (:group_id collection)]
                                                      ;; A family the user cannot take to AARK is no context at
                                                      ;; all, and a family is never one of its own collections.
                                                      (when (util/in? (map :id @permitted-groups) family-id)
                                                        (swap! chat-state merge
                                                               {:selected-family-id family-id}
                                                               (when (not= (:id collection) family-id)
                                                                 {:assignments (get-initial-ui-assignments
                                                                                 [{:collection_id (:id collection)
                                                                                   :collection collection
                                                                                   :main true}])}))))
                                                    (finish-seeding)))
                                           (p/error (fn [err]
                                                      (warn (str "Could not open AARK for collection "
                                                                 collection-id ": " err))
                                                      (finish-seeding)
                                                      nil))))]

        (r/create-class
         {:component-did-mount
          (fn []
            (controller/dispatch :permitted-groups :load {:collection-id nil
                                                          :admin-only false
                                                          :aark-enabled true
                                                          :on-success (fn []
                                                                        (cond
                                                                          conversation-id (chat-init conversation-id (fn [_]))
                                                                          context-collection-id (seed-context-collection context-collection-id)
                                                                          :else (default-family!)))}))
          :component-will-unmount
          (fn []
            (reset! unmounted? true)
            (some-> @reconnect-timer js/clearTimeout)
            (some-> @event-source .close))

          :reagent-render
          (fn []
            [:div
             [chat-view {:conversation-id (:conversation_id query-params)
                         :menu-open? (not (nil? (:conversation_id query-params)))
                         :open_chats_expanded (:open_chats_expanded query-params)
                         :completed_chats_expanded (:completed_chats_expanded query-params)
                         :initialized-chat initialized-chat}
              chat-state]])})))))

;; The page an AARK download link lands on (#3004), reached only when the user asks for a
;; page of their own: a modified click, a new tab, a bookmark, a reload.
(defn artifact-download [{:keys [id]}]
  ;; Cleared first: the controller is started once for the app, so a second visit would
  ;; otherwise open on the previous file's name and claim it as this one.
  (with-let [_ (do (controller/dispatch :artifact-download :clear {})
                   (controller/dispatch :artifact-download :save {:artifact-id id}))]
    (let [*saved (subscribe :artifact-download)]
      [:div.content.c-artifact-download
       [:div.o-media
        ;; No link back to a conversation: this page opens in a tab of its own, and the
        ;; conversation the user came from is still in the tab they left. The one thing
        ;; worth offering here is the same file again.
        (if-let [file-name (:file-name @*saved)]
          [:span
           "Downloaded. To download it again, click "
           ;; The real route as the href, so the link looks and behaves like every other
           ;; link -- hover shows where it goes, and a modified click opens it in a tab of
           ;; its own. A plain click is handled here, which downloads without reloading.
           [:a {:href (str "#/artifact/" id "/download")
                :on-click (fn [e]
                            ;; Only a plain click is answered here. A modified one belongs
                            ;; to the browser, which opens the same route in a tab of its
                            ;; own and downloads there.
                            (when (util/plain-click? e)
                              (.preventDefault e)
                              (controller/dispatch :artifact-download :save {:artifact-id id})))}
            file-name]
           "."]
          "Downloading the file...")]])))
